import type { Rule } from "../types";
import { makeFiring, mean } from "../util";

const EVIDENCE = "plews_2013_hrv_guided_training";

/**
 * Resting HR ≥ 5 bpm above 30d baseline for 3 consecutive mornings. Same
 * Plews-style monitoring frame as the HRV rule (RHR rises when HRV falls;
 * either signal warrants a load reduction).
 */
export const rhrElevated3d: Rule = {
  id: "rhr_elevated_3d_above_baseline",
  domain: "health.recovery",
  topics: ["rhr", "training_load"],
  evidence_refs: [EVIDENCE],
  title: () => "RHR elevated three mornings",
  evaluate(ctx) {
    if (ctx.vitals.length < 10) return null;
    const sortedDesc = [...ctx.vitals].sort((a, b) => (a.date < b.date ? 1 : -1));
    const last3 = sortedDesc.slice(0, 3);
    const baselineWindow = sortedDesc.slice(3, 33);

    const last3Vals = last3.map((r) => r.rhr_bpm);
    if (last3Vals.some((v) => v == null)) return null;

    const baseline = mean(baselineWindow.map((r) => r.rhr_bpm));
    if (baseline == null) return null;

    const deltas = last3Vals.map((v) => (v as number) - baseline);
    if (!deltas.every((d) => d >= 5)) return null;

    const avgDelta = mean(deltas) ?? 0;
    return makeFiring({
      rule_id: "rhr_elevated_3d_above_baseline",
      now: ctx.now,
      tz: ctx.tz,
      severity: "warn",
      message: `RHR averaging +${avgDelta.toFixed(1)}bpm above baseline ${baseline.toFixed(0)}bpm for three days. Likely accumulated fatigue or oncoming illness.`,
      evidence_refs: [EVIDENCE],
      factors: [
        {
          name: "rhr_delta_bpm",
          value: Number(avgDelta.toFixed(1)),
          contribution: "consider rest",
          evidence_ref: EVIDENCE,
        },
      ],
      target_surface: "health.recovery",
    });
  },
};
